// src/api/client.js
import axios from "axios";

// Base URL priority:
// 1) Vite env at build time (VITE_API_BASE_URL=http://127.0.0.1:8000/api)
// 2) window override at runtime (optional)
// 3) fallback to relative "/api" (for IIS reverse proxy)
const envBase =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  (typeof window !== "undefined" && window.VITE_API_BASE_URL) ||
  "/api";

// Strip trailing slash so we don't get /api//users etc.
const BASE_URL = envBase.replace(/\/+$/, "");


function getViewerEmail() {
  const direct =
    (localStorage.getItem("user_email") ||
      localStorage.getItem("email") ||
      "").trim();

  if (direct) return direct.toLowerCase();

  try {
    const u = JSON.parse(
      localStorage.getItem("user") ||
        localStorage.getItem("currentUser") ||
        "null"
    );
    if (u?.email) return String(u.email).trim().toLowerCase();
  } catch {}

  return "";
}


export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 600000,
});

api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("access_token");
  if (t) {
    cfg.headers = cfg.headers || {};
    cfg.headers.Authorization = `Bearer ${t}`;
  }

  // ✅ send viewer email for SFDC filtering
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    if (u?.email) {
      cfg.headers = cfg.headers || {};
      cfg.headers["X-User-Email"] = String(u.email).trim().toLowerCase();
    }
  } catch {}

  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const s = err?.response?.status;
    if (s === 401) {
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      if (
        typeof window !== "undefined" &&
        window.location.pathname !== "/login"
      ) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

// ----------------------------------------------------
// Generic helpers (used across SOW Analyzer UI)
// ----------------------------------------------------

export function apiGet(path, config = {}) {
  return api.get(path, config).then((r) => r.data);
}

export function apiPost(path, body = {}, config = {}) {
  return api.post(path, body, config).then((r) => r.data);
}

export function apiPut(path, body = {}, config = {}) {
  return api.put(path, body, config).then((r) => r.data);
}

export function apiDelete(path, config = {}) {
  return api.delete(path, config).then((r) => r.data);
}

export function apiJson(path, body = {}, method = "POST", config = {}) {
  return api({
    url: path,
    method,
    data: body,
    ...config,
  }).then((r) => r.data);
}

export function apiForm(path, formData, config = {}) {
  return api
    .post(path, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        ...(config.headers || {}),
      },
      ...config,
    })
    .then((r) => r.data);
}

// ----------------------------------------------------
// Error helper
// ----------------------------------------------------

export function getApiError(err) {
  const msg =
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.message ||
    "Request failed";
  return msg;
}

// ----------------------------------------------------
// URL normalization (avoid /api/api/...)
// ----------------------------------------------------

function normalizeUrl(url) {
  let u = String(url || "");
  if (!u.startsWith("/")) u = "/" + u;

  const base = String(BASE_URL || "").toLowerCase().replace(/\/+$/, "");
  const uLower = u.toLowerCase();

  if (base.endsWith("/api") && uLower.startsWith("/api/")) {
    return u.slice(4);
  }
  return u;
}

// ----------------------------------------------------
// RAG KB HELPERS (Agentic RAG module)
// ----------------------------------------------------

export function ragListCollections(projectId) {
  return api
    .get("/kb/collections", {
      params: projectId ? { project_id: projectId } : undefined,
    })
    .then((r) => r.data);
}

export function ragCreateCollection(payload) {
  return api.post("/kb/collections", payload).then((r) => r.data);
}

export function ragUploadDocuments(
  collectionId,
  { files = [], serverPaths = "", urls = "" }
) {
  const formData = new FormData();
  (files || []).forEach((f) => formData.append("files", f));
  if (serverPaths) formData.append("server_paths", serverPaths);
  if (urls) formData.append("urls", urls);

  return api
    .post(`/kb/${collectionId}/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 300000,
    })
    .then((r) => r.data);
}

export function ragBuildIndex(collectionId) {
  return ragStartIndex(collectionId);
}

export function ragSearch(collectionId, payload) {
  return api
    .post(`/search/collections/${collectionId}`, payload)
    .then((r) => r.data);
}

export function ragAsk(collectionId, projectId, payload) {
  return api
    .post(`/search/collections/${collectionId}/ask`, payload, {
      params: { project_id: projectId },
    })
    .then((r) => r.data);
}

export function ragAnalyticsOverview(collectionId) {
  return api
    .get(`/analytics/collections/${collectionId}/overview`)
    .then((r) => r.data);
}

export function ragAnalyticsRecent(collectionId, limit = 20) {
  return api
    .get(`/analytics/collections/${collectionId}/recent`, {
      params: { limit },
    })
    .then((r) => r.data);
}

export function ragStartIndex(collectionId) {
  return api
    .post(`/kb/${collectionId}/index/start`, {})
    .then((r) => r.data);
}

export function ragIndexProgress(collectionId) {
  return api
    .get(`/kb/${collectionId}/index/progress`)
    .then((r) => r.data);
}

export function ragListJobs(collectionId) {
  return api
    .get(`/kb/collections/${collectionId}/jobs`)
    .then((r) => r.data);
}

export function ragJobDetail(jobId) {
  return api.get(`/kb/jobs/${jobId}`).then((r) => r.data);
}

export function ragKbOverview() {
  return api.get("/kb/overview").then((r) => r.data);
}

export function ragListJobsForCollection(collectionId) {
  return api
    .get(`/kb/collections/${collectionId}/jobs`)
    .then((r) => r.data);
}


// ----------------------------------------------------
// SOW Template Builder API
// ----------------------------------------------------

export const sowTemplateApi = {
  list: (query = "", contractType = "") => {
    const qs = [];
    if (query) qs.push(`query=${encodeURIComponent(query)}`);
    if (contractType) qs.push(`contract_type=${encodeURIComponent(contractType)}`);
    qs.push("limit=50");
    return api.get(`/sow-templates${qs.length ? `?${qs.join("&")}` : ""}`).then((r) => r.data);
  },

  // Aliases for compatibility
  listTemplates: function(contractType = "") {
    return this.list("", contractType);
  },

  listTemplatesV2: function(query = "", contractType = "") {
    return this.list(query, contractType);
  },

  get: (id) => api.get(`/sow-templates/${id}`).then((r) => r.data),
  getTemplate: function(id) { return this.get(id); },

  create: (body) => api.post(`/sow-templates`, body).then((r) => r.data),
  createTemplate: function(body) { return this.create(body); },

  update: (id, body) => api.put(`/sow-templates/${id}`, body).then((r) => r.data),
  updateTemplate: function(id, body) { return this.update(id, body); },

  delete: (id) => api.delete(`/sow-templates/${id}`).then((r) => r.data),
  deleteTemplate: function(id) { return this.delete(id); },

  uploadDocx: (formData) =>
    api
      .post(`/sow-templates/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      })
      .then((r) => r.data),
  uploadTemplateDocx: function(formData) { return this.uploadDocx(formData); },

  downloadDocx: (id) =>
    api.get(`/sow-templates/${id}/download`, { responseType: "blob" }).then((r) => r.data),
  downloadTemplateDocx: function(id) { return this.downloadDocx(id); },
};

// ----------------------------------------------------
// SOW Draft API (Separate)
// ----------------------------------------------------


export const sowDraftApi = {
  // Opportunities
  listOpps: (query = "", limit = 100) => {
    const params = new URLSearchParams();

    // ✅ enforce visibility (backend filter)
    const viewerEmail =
      (localStorage.getItem("user_email") ||
        (() => {
          try {
            const u = JSON.parse(localStorage.getItem("user") || "null");
            return u?.email || "";
          } catch {
            return "";
          }
        })()
      ).trim();

    if (viewerEmail) params.append("user_email", viewerEmail);

    if (query) params.append("query", query);
    if (limit) params.append("limit", String(limit));

    const qs = params.toString();
    return api.get(`/opportunities${qs ? `?${qs}` : ""}`).then((r) => r.data);
  },

  getOpp: (id) => {
    const viewerEmail =
      (localStorage.getItem("user_email") ||
        (() => {
          try {
            const u = JSON.parse(localStorage.getItem("user") || "null");
            return u?.email || "";
          } catch {
            return "";
          }
        })()
      ).trim();

    const url = viewerEmail
      ? `/opportunities/${id}?user_email=${encodeURIComponent(viewerEmail)}`
      : `/opportunities/${id}`;

    return api.get(url).then((r) => r.data);
  },

  // Templates (for draft creation)
  listTemplates: (contractType = "") => {
    const params = new URLSearchParams();
    if (contractType) params.append("contract_type", contractType);
    params.append("limit", "50");
    return api.get(`/sow-templates?${params.toString()}`).then((r) => r.data);
  },

  listTemplatesV2: (query = "", contractType = "") => {
    const qs = [];
    if (query) qs.push(`query=${encodeURIComponent(query)}`);
    if (contractType) qs.push(`contract_type=${encodeURIComponent(contractType)}`);
    qs.push("limit=50");
    return api.get(`/sow-templates${qs.length ? `?${qs.join("&")}` : ""}`).then((r) => r.data);
  },

  // Draft CRUD
  listDrafts: (opportunityId = null) => {
    const params = new URLSearchParams();
    if (opportunityId) params.append("opportunity_id", String(opportunityId));
    params.append("limit", "500");
    return api.get(`/sow-drafts?${params.toString()}`).then((r) => r.data);
  },

  createDraft: (data) => api.post("/sow-drafts", data).then((r) => r.data),

  getDraft: (id) => api.get(`/sow-drafts/${id}`).then((r) => r.data),

  // Generate content with LLM + RAG (synchronous - original)
  generateDraft: (draftId, options = {}) =>
    api.post(`/sow-drafts/${draftId}/generate`, options).then((r) => r.data),

  // Generate content ASYNC (background) — avoids timeout
  generateDraftAsync: (draftId, options = {}) =>
    api.post(`/sow-drafts/${draftId}/generate-async`, options).then((r) => r.data),

  // Poll generation status
  getGenerationStatus: (draftId) =>
    api.get(`/sow-drafts/${draftId}/generate-status`).then((r) => r.data),

  // Regenerate single section
  regenerateSection: (draftId, options = {}) =>
    api.post(`/sow-drafts/${draftId}/regenerate-section`, options).then((r) => r.data),

  // Section management
  updateSection: (draftId, sectionId, data) =>
    api.put(`/sow-drafts/${draftId}/sections/${sectionId}`, data).then((r) => r.data),

  addSection: (draftId, data) =>
    api.post(`/sow-drafts/${draftId}/sections`, data).then((r) => r.data),

  deleteSection: (draftId, sectionId) =>
    api.delete(`/sow-drafts/${draftId}/sections/${sectionId}`).then((r) => r.data),

  reorderSections: (draftId, orderedSectionIds) =>
    api.post(`/sow-drafts/${draftId}/reorder`, { ordered_section_ids: orderedSectionIds }).then((r) => r.data),

  // Preview & Export
  preview: (draftId) => api.get(`/sow-drafts/${draftId}/preview`).then((r) => r.data),

  exportDocx: (draftId) =>
    api.post(`/sow-drafts/${draftId}/export/docx`, {}, { responseType: "blob" }).then((r) => r.data),

  // Google Docs
  publishGoogleDoc: (draftId, options = {}) =>
    api.post(`/sow-drafts/${draftId}/google-docs/publish`, options).then((r) => r.data),

  // Proposal Document Upload & Parsing
  parseProposals: (files) => {
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    return api
      .post("/sow-drafts/parse-proposals", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      })
      .then((r) => r.data);
  },

  // JOSS Resource Estimate
  fetchJossResources: (optyId) =>
    api.get(`/sow-drafts/joss/resource-estimate`, { params: { opty_id: optyId } }).then((r) => r.data),
};
// ----------------------------------------------------
// Default export (axios instance)
// ----------------------------------------------------

export default api;